/*
 * Reducer to fetch projects paginated information.
 */

import axios from 'axios';
import { endOfDay, startOfDay } from 'date-fns';
import { AsyncAction } from '..';
import { ProjectSummary } from '../../types/project';

export type Filter =
    | ''
    | 'upcoming'
    | 'past due'
    | 'not planned'
    | 'ready to inspect'
    | 'in progress'
    | 'awaiting report'
    | 'under review'
    | 'completed';

type State = {
    fetching: boolean;
    filter: Filter;
    interval: Interval;
    data: ProjectSummary[];
    error?: Error;
};

type Action =
    | { type: 'project/calendar/reset' }
    | {
          type: 'project/calendar/list';
          start?: { filter: Filter; interval: Interval };
          payload?: { data: ProjectSummary[] };
          error?: Error;
      };

const initialState: State = {
    fetching: false,
    filter: '',
    interval: { start: new Date(), end: new Date() },
    data: [],
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'project/calendar/reset': {
            return { ...initialState };
        }
        case 'project/calendar/list': {
            if (action.payload) return { ...state, fetching: false, ...action.payload };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true, ...action.start };
        }
        default:
            return state;
    }
};

const reset = (): Action => ({ type: 'project/calendar/reset' });

const list = (filter?: Filter, interval?: Interval, rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { projectCalendar } = getState();
    filter = filter ?? projectCalendar.filter;
    interval = interval ?? projectCalendar.interval;
    try {
        dispatch({ type: 'project/calendar/list', start: { filter, interval } });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'project/calendar/list': cancel } });
        const filterParams =
            filter === ''
                ? {}
                : filter === 'upcoming'
                ? { upcoming: 'True' }
                : filter === 'past due'
                ? { past_due: 'True' }
                : filter === 'not planned'
                ? { inspection_plan__isnull: 'True' }
                : { status: filter };
        const paginationParams = { pagination: 'False' };
        const intervalParams = { start_date: startOfDay(interval.start), end_date: endOfDay(interval.end) };
        const response = await axios.get<ProjectSummary[]>('/inspections/overview/', {
            params: { ...filterParams, ...paginationParams, ...intervalParams },
            cancelToken: cancel.token,
        });
        dispatch({ type: 'project/calendar/list', payload: { data: response.data } });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'project/calendar/list', error });
        if (rethrow) throw error;
    }
};

export const creators = { reset, list };
